import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createServiceCatalogItem,
  deleteServiceCatalogItem,
  listServiceCatalog,
  updateServiceCatalogItem,
  type ServiceCatalogItem,
} from "../api/serviceCatalog.ts";

export default function ServiceCatalogPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["service-catalog"], queryFn: () => listServiceCatalog() });
  const [draft, setDraft] = useState({
    code: "", name: "", name_he: "", default_duration_minutes: 60, default_price: 0, tax_rate_pct: 0,
  });

  const createMut = useMutation({
    mutationFn: () =>
      createServiceCatalogItem({
        code: draft.code,
        name: draft.name,
        name_he: draft.name_he || undefined,
        default_duration_minutes: Number(draft.default_duration_minutes),
        default_price: Number(draft.default_price),
        tax_rate_pct: Number(draft.tax_rate_pct),
      }),
    onSuccess: () => {
      setDraft({ code: "", name: "", name_he: "", default_duration_minutes: 60, default_price: 0, tax_rate_pct: 0 });
      qc.invalidateQueries({ queryKey: ["service-catalog"] });
    },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => updateServiceCatalogItem(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["service-catalog"] }),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteServiceCatalogItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["service-catalog"] }),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t("serviceCatalog.title")}</h1>

      <section className="card grid gap-2 md:grid-cols-7">
        <input className="input md:col-span-1" placeholder={t("serviceCatalog.col.code") ?? ""} value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
        <input className="input md:col-span-2" placeholder={t("serviceCatalog.col.name") ?? ""} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <input className="input md:col-span-2" placeholder={t("serviceCatalog.col.nameHe") ?? ""} value={draft.name_he} onChange={(e) => setDraft({ ...draft, name_he: e.target.value })} />
        <input className="input" type="number" placeholder="min" value={draft.default_duration_minutes} onChange={(e) => setDraft({ ...draft, default_duration_minutes: Number(e.target.value) })} />
        <input className="input" type="number" placeholder="$" value={draft.default_price} onChange={(e) => setDraft({ ...draft, default_price: Number(e.target.value) })} />
        <button className="btn-primary md:col-span-7" type="button" disabled={!draft.code || !draft.name || createMut.isPending} onClick={() => createMut.mutate()}>
          {t("serviceCatalog.add")}
        </button>
      </section>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">{t("serviceCatalog.col.code")}</th>
              <th className="px-4 py-2">{t("serviceCatalog.col.name")}</th>
              <th className="px-4 py-2">{t("serviceCatalog.col.duration")}</th>
              <th className="px-4 py-2">{t("serviceCatalog.col.price")}</th>
              <th className="px-4 py-2">{t("serviceCatalog.col.tax")}</th>
              <th className="px-4 py-2">{t("serviceCatalog.col.active")}</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>}
            {data?.items.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{t("serviceCatalog.empty")}</td></tr>}
            {data?.items.map((it: ServiceCatalogItem) => (
              <tr key={it.id}>
                <td className="px-4 py-2 font-mono text-xs">{it.code}</td>
                <td className="px-4 py-2 font-medium">{it.name}{it.name_he && <span className="ms-2 text-xs text-slate-500" dir="rtl">({it.name_he})</span>}</td>
                <td className="px-4 py-2 text-slate-600">{it.default_duration_minutes} min</td>
                <td className="px-4 py-2 text-slate-600">{it.default_price}</td>
                <td className="px-4 py-2 text-slate-600">{it.tax_rate_pct}%</td>
                <td className="px-4 py-2">
                  <input type="checkbox" checked={it.active} onChange={(e) => toggleMut.mutate({ id: it.id, active: e.target.checked })} />
                </td>
                <td className="px-4 py-2 text-right">
                  <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => delMut.mutate(it.id)}>
                    {t("common.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
